import { apiClient } from "./base/http";

export interface PaymentMethodEntry {
  method: string;
  amount: number;
}

export interface AssistantFeeEntry {
  assistant_id: string;
  amount: number;
}

export interface Booking {
  id: string;
  user_id: string;
  customer_id: string;
  staff_id: string;
  service_id: string | null;
  booking_group_id?: string | null;
  attendee_name?: string | null;
  booking_date: string;
  start_time: string;
  end_time: string;
  status: "pending" | "checked_in" | "no_show" | "paid" | "completed" | "cancelled";
  note: string | null;
  total_amount: number;
  deposit_amount: number;
  deposit_due_amount?: number;
  deposit_required?: boolean;
  deposit_paid: boolean;
  deposit_payment_method: string | null;
  deposit_payment_methods: PaymentMethodEntry[] | null;
  deposit_paid_at: string | null;
  payment_status: "unpaid" | "partial" | "paid";
  payment_method: string | null;
  payment_methods: PaymentMethodEntry[] | null;
  paid_amount: number;
  created_at: string;
  updated_at: string;
  assistant_ids?: string[] | null;
  assistant_fee_total?: number;
  assistant_fees?: Array<{ assistant_id: string; assistant_name: string; amount: number }> | null;
  manual_discount?: number;
  manual_discount_note?: string | null;
  loyalty_points_used?: number;
  loyalty_discount_amount?: number;
  transfer_reference?: string | null;
  line_confirmed_at?: string | null;
  customer_package_id?: string | null;
  package_name?: string | null;
  /** 這筆預約用的套票涵蓋的服務 ID（編輯存檔重算金額時，套票內服務不計價） */
  package_service_ids?: string[];
  // 紅利兌換活動（換到的服務／商品用於本次預約折抵）
  customer_redemption_id?: string | null;
  redemption_target_name?: string | null;
  redemption_value?: number | null;
  redemption_reward_type?: "service" | "product" | null;
  // 聯合查詢欄位
  customer_name?: string;
  customer_phone?: string;
  customer_email?: string;
  customer_line_picture_url?: string | null;
  staff_name?: string;
  staff_display_name?: string;
  staff_assign_source?: string;
  service_name?: string;
  service_price?: number;
  service_duration?: number;
  // 設備分配
  device_id?: string | null;
  device_name?: string | null;
  device_color?: string | null;
  // 分店
  shop_id?: string | null;
  // 明細資料
  services?: BookingService[];
  products?: BookingProduct[];
  coupons?: BookingCoupon[];
  /** 待退款：status=cancelled 且還有一筆待審核的退款申請（取消已收款預約時後端自動建立） */
  has_pending_refund?: boolean;
  /** 同時段多顧客服務：額外加入同一筆預約的顧客（主顧客仍是 customer_id） */
  is_multi_customer?: boolean;
  max_attendees?: number | null;
  participants?: BookingParticipant[];
}

export interface BookingParticipant {
  customer_id: string;
  name: string;
  phone?: string | null;
}

export interface BookingCoupon {
  id: string;
  booking_id: string;
  coupon_id: string;
  code: string;
  discount_amount: number;
}

export interface BookingService {
  id?: string;
  booking_id?: string;
  staff_id?: string | null;
  service_id: string;
  service_name: string;
  price: number;
  quantity: number;
  discount_amount?: number;
  subtotal: number;
  staff_name?: string | null;
  staff_display_name?: string | null;
}

export interface BookingProduct {
  id?: string;
  booking_id?: string;
  product_id: string;
  product_name: string;
  price: number;
  quantity: number;
  discount_amount?: number;
  subtotal: number;
}

export interface BookingActivity {
  id: string;
  booking_id: string;
  staff_id: string | null;
  customer_id: string | null;
  shop_id: string | null;
  action: string;
  status: Booking["status"] | null;
  note: string | null;
  metadata?: Record<string, unknown> | null;
  created_at: string;
  customer_name?: string | null;
  staff_display_name?: string | null;
  staff_name?: string | null;
  staff_assign_source?: string | null;
}

export interface GetBookingsParams {
  date?: string;
  date_from?: string;
  date_to?: string;
  staff_id?: string;
  shop_id?: string;
  status?: Booking["status"] | `${Booking["status"]},${Booking["status"]}`;
  customer_id?: string;
  deposit_pending?: boolean;
  /** 待退款：已取消、但還有一筆待審核退款申請的預約（取消已收款預約時後端自動建立） */
  refund_pending?: boolean;
  page?: number;
  limit?: number;
  sort?: "asc" | "desc";
}

export interface CreateBookingPayload {
  shop_id?: string | null;
  customer_id?: string;
  customer_data?: {
    name: string;
    phone?: string;
    email?: string;
  };
  staff_id: string;
  service_id?: string;
  attendee_name?: string | null;
  booking_date: string;
  start_time: string;
  end_time: string;
  services?: Array<{
    service_id: string;
    quantity?: number;
    price?: number;
    staff_id?: string | null;
  }>;
  products?: Array<{
    product_id: string;
    quantity?: number;
    price?: number;
  }>;
  note?: string;
  status?: "pending" | "checked_in" | "no_show" | "paid" | "completed" | "cancelled";
  assistant_ids?: string[];
  deposit_amount?: number;
  deposit_due_amount?: number;
  deposit_required?: boolean;
  deposit_paid?: boolean;
  deposit_payment_method?: string | null;
  deposit_payment_methods?: PaymentMethodEntry[] | null;
  transfer_reference?: string | null;
  manual_discount?: number;
  manual_discount_note?: string | null;
  /** 顧客兌換到的服務/商品折抵券 id（customer_reward_redemptions.id） */
  /** 使用顧客的套票（套票內服務免費、扣 1 次）；必須是預約顧客本人的套票 */
  customer_package_id?: string | null;
  /** 多人同時段服務要帶入的同行者（必須是該套票的共用成員） */
  participant_customer_ids?: string[];
  customer_redemption_id?: string | null;
  skip_conflict_check?: boolean;
  payment_method?: string | null;
  assistant_fees?: AssistantFeeEntry[];
  device_id?: string | null;
  buffer_minutes?: number;
}

export interface UpdateBookingPayload {
  shop_id?: string | null;
  customer_id?: string;
  customer_data?: {
    name: string;
    phone?: string;
    email?: string;
  };
  staff_id?: string;
  service_id?: string;
  attendee_name?: string | null;
  booking_date?: string;
  start_time?: string;
  end_time?: string;
  status?: "pending" | "checked_in" | "no_show" | "paid" | "completed" | "cancelled";
  note?: string;
  services?: Array<{
    service_id: string;
    quantity?: number;
    price?: number;
    staff_id?: string | null;
  }>;
  products?: Array<{
    product_id: string;
    quantity?: number;
    price?: number;
  }>;
  assistant_ids?: string[];
  deposit_amount?: number;
  deposit_due_amount?: number;
  deposit_required?: boolean;
  deposit_paid?: boolean;
  deposit_payment_method?: string | null;
  deposit_payment_methods?: PaymentMethodEntry[] | null;
  transfer_reference?: string | null;
  payment_method?: string | null;
  manual_discount?: number;
  manual_discount_note?: string | null;
  /** 顧客兌換到的服務/商品折抵券 id（customer_reward_redemptions.id）；null 明確清除，不帶則沿用現況 */
  customer_redemption_id?: string | null;
  skip_conflict_check?: boolean;
  device_id?: string | null;
  buffer_minutes?: number;
  /** Skip the "deposit just confirmed" LINE booking-confirmation push for this save. */
  suppress_line_notification?: boolean;
}

export interface BookingsResponse {
  success: boolean;
  data: Booking[];
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface BookingResponse {
  success: boolean;
  data: Booking;
}

export interface BookingActivityResponse {
  success: boolean;
  data: BookingActivity[];
}

export interface AvailableStaff {
  id: string;
  name: string;
  display_name?: string | null;
  avatar?: string | null;
  position?: string | null;
  phone?: string | null;
  email?: string | null;
}

export interface AvailableStaffResponse {
  success: boolean;
  staffs: AvailableStaff[];
}

export const fetchBookings = async (params?: GetBookingsParams): Promise<Booking[]> => {
  const { data } = await apiClient.get<BookingsResponse>("/api/bookings", { params });
  return data.data;
};

export const fetchBookingsPaged = async (
  params?: GetBookingsParams
): Promise<{ bookings: Booking[]; total: number; page: number; limit: number }> => {
  const { data } = await apiClient.get<BookingsResponse>("/api/bookings", { params });
  return {
    bookings: data.data,
    total: Number(data.pagination?.total ?? data.data.length),
    page: Number(data.pagination?.page ?? params?.page ?? 1),
    limit: Number(data.pagination?.limit ?? params?.limit ?? data.data.length),
  };
};

export const fetchBooking = async (id: string): Promise<Booking> => {
  const { data } = await apiClient.get<BookingResponse>(`/api/bookings/${id}`);
  return data.data;
};

export const createBooking = async (payload: CreateBookingPayload): Promise<Booking> => {
  const { data } = await apiClient.post<BookingResponse>("/api/bookings", payload);
  return data.data;
};

export const updateBooking = async (id: string, payload: UpdateBookingPayload): Promise<void> => {
  await apiClient.put(`/api/bookings/${id}`, payload);
};

export const updateBookingStatus = async (id: string, status: string, paymentMethod?: string, paymentMethods?: PaymentMethodEntry[]): Promise<{ autoRefundCreated?: boolean }> => {
  const payload: Record<string, unknown> = { status };
  if (paymentMethod) payload.payment_method = paymentMethod;
  if (paymentMethods) payload.payment_methods = paymentMethods;
  const res = await apiClient.patch(`/api/bookings/${id}/status`, payload);
  return res.data ?? {};
};

export const updateBookingDeposit = async (
  id: string,
  payload: {
    deposit_amount?: number;
    deposit_paid?: boolean;
    deposit_payment_method?: string | null;
    deposit_payment_methods?: PaymentMethodEntry[] | null;
    deposit_due_amount?: number;
    deposit_required?: boolean;
    transfer_reference?: string | null;
    payment_status?: "unpaid" | "partial" | "paid";
    note?: string | null;
    deposit_paid_at?: string | null;
    /** Skip the "deposit just confirmed" LINE booking-confirmation push for this call. */
    suppress_line_notification?: boolean;
  }
): Promise<{ success: boolean; lineNotified?: boolean }> => {
  const { data } = await apiClient.patch<{ success: boolean; lineNotified?: boolean }>(`/api/bookings/${id}`, payload);
  return data;
};

export const sendBookingConfirmationNow = async (id: string): Promise<{ success: boolean; message?: string }> => {
  const { data } = await apiClient.post<{ success: boolean; message?: string }>(`/api/bookings/${id}/send-confirmation`);
  return data;
};

export const updateBookingPayableAmount = async (
  id: string,
  payload: {
    manual_discount: number;
    manual_discount_note?: string | null;
  }
): Promise<{ success: boolean }> => {
  const { data } = await apiClient.patch<{ success: boolean }>(`/api/bookings/${id}`, payload);
  return data;
};

export const deleteBooking = async (id: string): Promise<void> => {
  await apiClient.delete(`/api/bookings/${id}`);
};

export const addBookingParticipant = async (id: string, customerId: string): Promise<BookingParticipant[]> => {
  const { data } = await apiClient.post<{ data: { participants: BookingParticipant[] } }>(`/api/bookings/${id}/participants`, { customer_id: customerId });
  return data.data.participants;
};

export const removeBookingParticipant = async (id: string, customerId: string): Promise<BookingParticipant[]> => {
  const { data } = await apiClient.delete<{ data: { participants: BookingParticipant[] } }>(`/api/bookings/${id}/participants/${customerId}`);
  return data.data.participants;
};

export const checkout = async (
  id: string,
  payload: { payment_method?: string; payment_methods?: PaymentMethodEntry[]; amount?: number; note?: string; assistant_fees?: AssistantFeeEntry[]; loyalty_points_to_use?: number }
): Promise<void> => {
  await apiClient.post(`/api/bookings/${id}/checkout`, payload);
};

export const applyBookingCoupon = async (
  id: string,
  code: string
): Promise<{ discount_amount: number; total_after: number }> => {
  const { data } = await apiClient.post(`/api/bookings/${id}/apply-coupon`, { code });
  return data.data;
};

export const removeBookingCoupon = async (id: string): Promise<void> => {
  await apiClient.delete(`/api/bookings/${id}/remove-coupon`);
};

export const payWithWallet = async (
  id: string,
  payload: { amount: number; note?: string }
): Promise<void> => {
  await apiClient.post(`/api/bookings/${id}/pay-with-wallet`, payload);
};

export const fetchBookingActivity = async (params?: { limit?: number; shop_id?: string }): Promise<BookingActivity[]> => {
  const { data } = await apiClient.get<BookingActivityResponse>("/api/bookings/activity", { params });
  return data.data;
};

export const fetchAvailableStaff = async (params: {
  booking_date: string;
  start_time: string;
  end_time: string;
  exclude_booking_id?: string;
}): Promise<AvailableStaff[]> => {
  const { data } = await apiClient.get<AvailableStaffResponse>("/api/bookings/available-staff", { params });
  return data.staffs;
};

export interface BookingLoyaltyRedemptionInfo {
  enabled: boolean;
  rate: number;
  owner_customer_id: string | null;
  owner_name: string | null;
  /** true = 出點帳戶是多人預約的預約者（聯絡人），不是這筆預約自己的顧客 */
  is_group_contact: boolean;
  balance: number;
}

export const fetchBookingLoyaltyRedemption = async (id: string): Promise<BookingLoyaltyRedemptionInfo> => {
  const { data } = await apiClient.get(`/api/bookings/${id}/loyalty-redemption`);
  return data.data;
};

export interface ScanCheckInBooking {
  id: string;
  customerName: string | null;
  serviceName: string | null;
  staffName: string | null;
  deviceName: string | null;
  /** 設備租借（無人員）：報到即「使用中」 */
  isRental: boolean;
  bookingDate: string;
  startTime: string;
  endTime: string;
  status: string;
}

export interface ScanCheckInResult {
  success: boolean;
  message: string;
  booking?: ScanCheckInBooking;
}

/**
 * 從顧客報到 QR 的內容解析出預約 ID 與驗證碼。
 * QR 內容格式：https://<網域>/check-in/<預約ID>?token=<驗證碼>；不是報到 QR 回 null。
 */
export const parseCheckInQr = (raw: string): { bookingId: string; token: string } | null => {
  const match = /\/check-in\/([^/?#\s]+)\?(?:[^#\s]*&)?token=([^&#\s]+)/.exec(String(raw ?? "").trim());
  if (!match) return null;
  return { bookingId: decodeURIComponent(match[1]), token: decodeURIComponent(match[2]) };
};

/** 店家用後台 App 掃顧客報到 QR：需登入，只能報到本店預約。失敗時也回傳後端訊息（不丟例外） */
export const scanCheckInBooking = async (bookingId: string, token: string): Promise<ScanCheckInResult> => {
  try {
    const res = await apiClient.post<ScanCheckInResult>("/api/bookings/check-in-scan", { bookingId, token });
    return res.data;
  } catch (error) {
    const data = (error as { response?: { data?: Partial<ScanCheckInResult> } })?.response?.data;
    return { success: false, message: data?.message || "報到失敗，請稍後再試", booking: data?.booking };
  }
};
